<!-- Dealer marketplace (plate-detail-v2). Prototype "PlateX 33.3" styling over
     the existing dealer comparison data + behaviour. Hidden when there are no
     live listings. -->
<section
  class="pdv2-marketplace"
  *ngIf="loading || totalCount > 0"
  id="sec-compare"
>
  <div class="mp-head">
    <div class="mp-eyebrow">Dealer marketplace</div>
    <h2 class="mp-title">{{ title }}</h2>
    <p class="mp-sub">{{ supportingCopy }}</p>
  </div>

  <!-- Explanatory accordion. Generic guidance only; it does NOT override the
       per-seller attributes shown in each row. -->
  <details class="mp-disclosure" *ngIf="loading || rows.length">
    <summary>
      <span class="mpd-head">
        <span class="mpd-title">What's included in dealer pricing?</span>
        <!-- Desktop only, collapsed only: a reason to open it. -->
        <span class="mpd-preview">Finance, transfer services, physical plates and support can vary by dealer.</span>
      </span>
      <svg class="mpd-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </summary>
    <div class="mpd-body">
      <p class="mpd-intro">
        Dealer prices can include services beyond the registration itself, such as
        the ones below. What each seller offers varies, so check each listing.
      </p>
      <div class="mpd-list">
        <div class="mpd-item" *ngFor="let item of includedServices">
          <svg
            class="tick"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>{{ item }}</span>
        </div>
      </div>
    </div>
  </details>

  <div class="mp-controls" *ngIf="loading || rows.length">
    <!-- The listing count is in the title; only the transient live-price
         check is reported here. -->
    <!-- No count by default (the title has it); a filtered view says how many
         sellers still match. -->
    <div class="mp-count mp-filtered" *ngIf="activeFilterCount > 0">
      Showing {{ filteredCount }} of {{ totalCount }} seller{{ totalCount === 1 ? '' : 's' }}
    </div>
    <div class="mp-count live-progress" *ngIf="checkingCount > 0">
      Checking {{ checkingCount }} dealer{{ checkingCount === 1 ? '' : 's' }} for live prices…
    </div>
    <div class="mp-tools">
      <button
        type="button"
        class="mp-filters-btn"
        [class.is-open]="filtersOpen"
        [class.has-active]="activeFilterCount > 0"
        (click)="toggleFilters()"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="7" y1="12" x2="17" y2="12" />
          <line x1="10" y1="18" x2="14" y2="18" />
        </svg>
        <span class="mp-filters-label">Filters</span>
        <span class="mp-filters-badge" *ngIf="activeFilterCount > 0">{{
          activeFilterCount
        }}</span>
      </button>
      <!-- Desktop has room for the fuller wording; phones keep the short one. -->
      <label class="mp-sort mp-sort-wide">
        <span>Sort by</span>
        <select [value]="sort" (change)="setSort($any($event.target).value)">
          <option value="price-asc">Price: low to high</option>
          <option value="price-desc">Price: high to low</option>
          <option value="trust">Trust rating</option>
        </select>
      </label>
      <div class="mp-sort mp-sort-narrow">
        <select
          aria-label="Sort sellers"
          [value]="sort"
          (change)="setSort($any($event.target).value)"
        >
          <option value="price-asc">Lowest price</option>
          <option value="price-desc">Highest price</option>
          <option value="trust">Trust rating</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Filters panel — reuses the EXACT predicates from the existing tp-sellers
       comparison (in-memory only; no new backend calls). -->
  <div class="mp-filter-panel" *ngIf="filtersOpen">
    <div class="mp-filter-grid">
      <label class="mp-filter-opt">
        <input type="checkbox" [(ngModel)]="filters.standardPlates" />
        <span>Standard plates supplied</span>
      </label>
      <label class="mp-filter-opt">
        <input type="checkbox" [(ngModel)]="filters.managedTransfer" />
        <span>Managed transfer</span>
      </label>
      <label class="mp-filter-opt">
        <input type="checkbox" [(ngModel)]="filters.offersKlarna" />
        <span>Offers Klarna</span>
      </label>
      <label class="mp-filter-opt">
        <input type="checkbox" [(ngModel)]="filters.offersBespokeFinance" />
        <span>Bespoke finance</span>
      </label>
      <label class="mp-filter-opt mp-filter-rating">
        <span>Minimum rating</span>
        <select [(ngModel)]="filters.rating">
          <option value="any">Any</option>
          <option value="3">3★ and up</option>
          <option value="4">4★ and up</option>
          <option value="5">5★ only</option>
        </select>
      </label>
    </div>
    <button
      type="button"
      class="mp-filter-clear"
      *ngIf="activeFilterCount > 0"
      (click)="clearFilters()"
    >
      Clear all
    </button>
  </div>

  <!-- column labels -->
  <div class="mp-thead" *ngIf="!loading && totalCount > 0">
    <span>Seller</span>
    <span class="col-c">Finance</span>
    <span class="col-c">DVLA recognised</span>
    <span class="col-c">Managed transfer</span>
    <span class="col-c">Standard plates</span>
    <span class="col-r">Price</span>
  </div>

  <div class="dealer-list">
    <!-- DVLA new-release offer (27 pre-sale), from DVLA's snapshot. -->
    <div class="dealer-row" *ngIf="dvla as d">
      <div class="seller">
        <div class="seller-top">
          <span class="seller-name">DVLA</span>
        </div>
        <div class="seller-rating">
          <span class="rating-na">{{ d.note }}</span>
        </div>
      </div>
      <div class="attr">
        <span class="attr-ico no">
          <ng-container *ngTemplateOutlet="crossIco"></ng-container>
        </span>
        <span class="attr-text">Offers finance</span>
      </div>
      <div class="attr">
        <span class="attr-ico yes">
          <ng-container *ngTemplateOutlet="checkIco"></ng-container>
        </span>
        <span class="attr-text">DVLA recognised</span>
      </div>
      <div class="attr">
        <span class="attr-ico no">
          <ng-container *ngTemplateOutlet="crossIco"></ng-container>
        </span>
        <span class="attr-text">Managed transfer</span>
      </div>
      <div class="attr">
        <span class="attr-ico no">
          <ng-container *ngTemplateOutlet="crossIco"></ng-container>
        </span>
        <span class="attr-text">Standard plates</span>
      </div>
      <div class="price-cell">
        <div class="price-top">
          <span class="price-amt">From {{ d.price | currency : 'GBP' : 'symbol' : '1.0-0' }}</span>
        </div>
        <a class="btn-visit" [href]="d.href" target="_blank" rel="noopener">{{ d.cta }}</a>
      </div>
    </div>
    <div class="dealer-row" *ngFor="let r of visibleRows">
      <!-- seller: name + existing Trustpilot CDN stars -->
      <div class="seller">
        <div class="seller-top">
          <span class="seller-name">{{ r.viewName }}</span>
        </div>
        <div class="seller-rating">
          <ng-container *ngIf="hasTrust(r); else noTrust">
            <img class="tp-img" [src]="trustImage(r)" (click)="openTrustpilot(r)" />
            <a class="rating-count" (click)="openTrustpilot(r)"
              >{{ r.trustPilotRatingCount | number : '1.0-0' }} reviews</a
            >
          </ng-container>
          <ng-template #noTrust>
            <span class="rating-na">No rating yet</span>
          </ng-template>
        </div>
      </div>

      <!-- finance -->
      <div class="attr">
        <span class="attr-ico" [class.yes]="r.finance" [class.no]="!r.finance">
          <ng-container
            *ngTemplateOutlet="r.finance ? checkIco : crossIco"
          ></ng-container>
        </span>
        <span class="attr-text">Offers finance</span>
      </div>

      <!-- DVLA recognised (recognised for all live sellers) -->
      <div class="attr">
        <span class="attr-ico yes">
          <ng-container *ngTemplateOutlet="checkIco"></ng-container>
        </span>
        <span class="attr-text">DVLA recognised</span>
      </div>

      <!-- managed transfer -->
      <div class="attr">
        <ng-container *ngIf="hasFee(r.transferService); else transferNo">
          <span class="attr-fee" [class.is-free]="r.transferService === 0">{{
            feeLabel(r.transferService)
          }}</span>
        </ng-container>
        <ng-template #transferNo>
          <span class="attr-ico no">
            <ng-container *ngTemplateOutlet="crossIco"></ng-container>
          </span>
        </ng-template>
        <span class="attr-text">Managed transfer</span>
      </div>

      <!-- standard plates -->
      <div class="attr">
        <ng-container *ngIf="hasFee(r.physicalPlatePrice); else platesNo">
          <span class="attr-fee" [class.is-free]="r.physicalPlatePrice === 0">{{
            feeLabel(r.physicalPlatePrice)
          }}</span>
        </ng-container>
        <ng-template #platesNo>
          <span class="attr-ico no">
            <ng-container *ngTemplateOutlet="crossIco"></ng-container>
          </span>
        </ng-template>
        <span class="attr-text">Standard plates</span>
      </div>

      <!-- price + CTA -->
      <div class="price-cell">
        <div class="price-top">
          <span class="price-amt" [class.is-checking]="liveState[r.site] === 'checking'">{{ r.total | currency : 'GBP' : 'symbol' : '1.0-0' }}</span>
        </div>
        <span class="live-chip checking" *ngIf="liveState[r.site] === 'checking'" aria-live="polite"><i class="live-spin" aria-hidden="true"></i>Checking live price</span>
        <span class="live-chip live" *ngIf="liveState[r.site] === 'live'" aria-live="polite">Live price</span>
        <span class="live-chip last-seen" *ngIf="liveState[r.site] === 'last-seen'" [title]="lastSeenTitle(r.site)">{{ lastSeenLabel(r.site) }}</span>
        <button type="button" class="btn-visit" (click)="visit(r)">
          Visit dealer
        </button>
      </div>
    </div>
  </div>

  <button
    type="button"
    class="show-more"
    *ngIf="remaining > 0"
    (click)="showMore()"
  >
    Show {{ remaining }} more listings
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.6"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </button>
</section>

<!-- shared attribute icons -->
<ng-template #checkIco>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2.6"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
</ng-template>
<ng-template #crossIco>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2.6"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
</ng-template>
